<template>
  <v-container id="billing-detail-form">
    <v-card class="mx-4 mt-0">
      <v-container>
        <v-card-title class="billing-detail-form__title mb-5 mt-4 ml-4">
          Replace Payment Method
        </v-card-title>
        <v-form v-model="valid">
          <v-col class="px-12">
            <fieldset>
              <v-container>
                <legend class="mb-2">
                  <v-icon
                    class="pt-1"
                    size="23"
                    color="#3C3333"
                  >
                    $faCreditCard
                  </v-icon>
                  <h2 style="font-weight:400">
                    Card Info
                  </h2>
                </legend>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="name"
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Cardholder's Name"
                      required
                      :rules="[val => (!!val && !Number(val)) || `Please enter the cardholder's name`]"
                    />
                  </v-col>
                </v-row>
                <v-row class="card-row">
                  <v-col class="py-0">
                    <stripe-element-card
                      ref="cardElementRef"
                      :pk="publishableKey"
                      :hide-postal-code="true"
                      :element-style="cardStyle"
                      @element-change="checkCard"
                    />
                  </v-col>
                </v-row>
              </v-container>
              <v-container>
                <v-row class="mb-2 px-3">
                  <v-col
                    cols="6"
                    class="pa-0"
                  >
                    <legend>
                      <v-icon
                        class="pt-1"
                        size="23"
                        color="#3C3333"
                      >
                        $faHome
                      </v-icon>
                      <h2 style="font-weight:400">
                        Billing Address
                      </h2>
                    </legend>
                  </v-col>
                  <v-col
                    cols="6"
                    class="pa-0 d-flex justify-end"
                  >
                    <v-checkbox
                      v-model="useCurrentAddress"
                      label="Use Current Billing Address"
                      hide-details="true"
                      class="mt-0 pt-3"
                      dense
                      :disabled="!hasPaymentMethod"
                      @change="toggleCurrentAddress"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="address1"
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Address Line 1"
                      required
                      :rules="[val => (!!val && !Number(val)) || `Please enter a valid billing address`]"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="address2"
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Address Line 2 (Optional)"
                    />
                  </v-col>
                </v-row>
                <v-row class="mx-0">
                  <v-col class="py-0 px-0 mr-2">
                    <v-text-field
                      v-model="city"
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="City"
                      required
                      :rules="[val => (!!val && !Number(val)) || 'Please enter a city']"
                    />
                  </v-col>
                  <v-col class="py-0 pl-0 pr-0 mr-2">
                    <v-select
                      v-if="countriesWithRegions.includes(country)"
                      v-model="region"
                      class="prop-field"
                      color="primary"
                      append-icon="$faChevronDown"
                      dense
                      filled
                      :label="country === 'USA' ? 'State' : 'Region'"
                      required
                      :items="regions"
                      :rules="[val => (!!val && !Number(val)) || 'Please select a region']"
                    />
                    <v-text-field
                      v-else
                      v-model="region"
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      label="Region"
                      required
                      :rules="[val => (!!val && !Number(val)) || 'Please enter a region']"
                    />
                  </v-col>
                  <v-col class="py-0 pl-0 pr-0">
                    <v-text-field
                      v-model="postalCode"
                      class="prop-field"
                      color="primary"
                      dense
                      filled
                      required
                      label="Zip Code"
                      :rules="[val => (!!val || 'Please enter a zip code')]"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    class="py-0"
                  >
                    <v-autocomplete
                      v-model="country"
                      class="prop-field"
                      color="primary"
                      append-icon="$faChevronDown"
                      dense
                      filled
                      label="Country"
                      required
                      placeholder="Start typing your country to search"
                      :items="countries"
                      :rules="[val => !!val || 'Please select a country']"
                    >
                      <template #item="{item, on, attrs}">
                        <v-list-item
                          v-bind="attrs"
                          class="d-flex align-center"
                          v-on="on"
                        >
                          <div
                            class="d-flex flex-column"
                            style="width:100%"
                          >
                            <div class="d-flex align-center">
                              <country-flag
                                :country="item.value.toLowerCase()"
                                size="small"
                              />
                              <span class="ml-2">{{ item.text }}</span>
                            </div>
                            <v-divider
                              v-if="item.text === 'United Kingdom'"
                              class="my-2"
                            />
                          </div>
                        </v-list-item>
                      </template>
                      <!-- eslint-disable-next-line -->
                      <template #selection="{item, index}">
                        <country-flag
                          :country="item.value.toLowerCase()"
                          size="small"
                        />
                        <span class="ml-2">{{ item.text }}</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-container>
            </fieldset>
          </v-col>
          <v-row class="px-12">
            <v-col class="py-0 px-6">
              <div class="button-group">
                <v-btn
                  color="secondary"
                  @click="onCancelForm"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="isLoading || !valid || !cardComplete"
                  :loading="isLoading"
                  @click.prevent="updatePaymentMethod"
                >
                  Save Payment Method
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { StripeElementCard } from '@vue-stripe/vue-stripe'
  import { regions as regionData } from '@/i18n'
  import countryListIso3 from '../../helpers/country-list-iso3.json'
  import countryListIso2 from '../../helpers/country-list-iso2.json'
  import CountryFlag from 'vue-country-flag'

  export default {
    name: 'BillingUpdate',
    components: {
      StripeElementCard,
      CountryFlag,
    },
    data () {
      this.publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
      return {
        valid: false,
        cardComplete: false,
        useCurrentAddress: false,
        countriesWithRegions: ['US', 'USA', 'CA', 'CAN'],
        isLoading: false,
        name: '',
        address1: '',
        address2: '',
        city: '',
        region: '',
        postalCode: '',
        country: 'USA',
        cardStyle: {
          base: {
            fontFamily: 'Lato',
            fontSize: '16px',
            '::placeholder': {
              fontSize: '14px',
            },
          },
        },
      }
    },
    computed: {
      ...mapGetters({
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        hasBillingEnabled: 'Billing/currentOrgBillingEnabled',
        paymentMethod: 'Billing/defaultPaymentMethod',
      }),
      regions () {
        let alpha2 = this.country.toLowerCase()

        if (alpha2 === 'usa') alpha2 = 'us'
        else if (alpha2 === 'can') alpha2 = 'ca'

        if (regionData[alpha2] !== undefined) {
          return regionData[alpha2].states.map(({ name, alpha2 }) => ({
            text:
              name,
            value: alpha2,
          }))
        } else return regionData.us.states.map(({ name, alpha2 }) => ({ text: name, value: alpha2 }))
      },
      countries () {
        let countryList

        if (this.country === 'US' || this.country === 'CA') countryList = countryListIso2
        else countryList = countryListIso3

        return Object.values(countryList).map(({ name, code }) => ({ text: name, value: code }))
      },
      hasPaymentMethod () {
        if (this.paymentMethod) {
          return true
        } else {
          return false
        }
      },
    },
    watch: {
      currentSelectedOrg (newOrg, oldOrg) {
        if (newOrg.id !== oldOrg.id) {
          this.returnToBillingDetail()
        }
      },
    },
    created () {
      if (!this.hasBillingEnabled) {
        this.returnToBillingDetail()
      }
    },
    methods: {
      returnToBillingDetail () {
        this.$router.push('/billing')
      },
      onCancelForm () {
        this.returnToBillingDetail()
      },
      checkCard (e) {
        if (e.complete) {
          this.cardComplete = true
        }
      },
      toggleCurrentAddress () {
        if (this.useCurrentAddress) {
          const address = this.paymentMethod.billingAddress

          this.address1 = address.address1
          this.address2 = address.address2
          this.city = address.city
          this.region = address.state
          this.postalCode = address.postalCode
          this.country = address.country
        } else {
          this.address1 = ''
          this.address2 = ''
          this.city = ''
          this.region = ''
          this.postalCode = ''
          this.country = 'USA'
        }
      },
      updatePaymentMethod () {
        this.isLoading = true

        const cardElement = this.$refs.cardElementRef

        const cardData = {
          type: 'card',
          usage: 'reusable',
          owner: {
            name: this.name,
            address: {
              line1: this.address1,
              city: this.city,
              state: this.region,
              postal_code: this.postalCode,
              country: this.country,
            },
          },
        }

        if (this.address2) {
          cardData.owner.address.line2 = this.address2
        }

        cardElement.stripe.createSource(cardElement.element, cardData)
          .then(response => {
            if (response && response.source && response.source.id) {
              const payload = {
                orgID: this.currentSelectedOrg.id,
                sourceID: response.source.id,
              }

              this.$store.dispatch('Billing/attachSourceToOrg', payload)
                .then(() => {
                  this.isLoading = false
                  this.returnToBillingDetail()
                })
                .catch(() => {
                  this.isLoading = false
                  this.cardComplete = false
                  cardElement.focus()
                })
            }
          })
          .catch(err => {
            const payload = {
              type: 'error',
              text: err,
            }

            this.$store.dispatch('updateMessages', payload, { root: true })

            this.cardComplete = false
            this.isLoading = false
            cardElement.focus()
          })
      },
    },
  }
</script>

<style lang="scss">
  #billing-detail-form {
    .v-select.v-input--dense .v-select__selection--comma {
      margin: 0 4px 10px 0;
    }

    .card-row {
      height: 78px;
    }

    .StripeElement {
      background-color: $na-dark-cream;
      border-bottom-color: rgb(118, 118, 118);
      min-height: 52px;
      padding-top: 16px;
      font-weight: 400;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      box-shadow: none;

      &:hover {
        border-bottom-color: $primary;
      }

      &.StripeElement--focus {
        border-bottom: 2px solid $primary;
      }

      &.StripeElement--invalid {
        color: $error;
        border-left: 0;
        border-right: 0;
        border-top: 0;
        border-bottom-color: $error
      }
    }

    #stripe-element-errors {
      color: $error;
      font-size: 12px;
      padding-left: 12px;
      font-family: inherit;
    }
  }

  .billing-detail-form__title {
    font-family: "Degular", sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 34px;
    line-height: 24px;
    color: #535353;
  }

  fieldset {
    border: none;
    padding: 0;
    margin: 0;
  }

  legend {
    left: 20px;
    display: flex;
    align-items: center;

    .v-icon {
      margin-right: 11px;
    }

    span:last-child {
      font-size: 24px
    }
  }

  .button-group {
    text-align: right;
    margin-bottom: 20px;
  }

  // Turn off number spinner UI
  // Chrome, Safari, Edge, Opera
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  // Firefox
  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
